import {
  type AppShellMode,
  type CoreFeatureFlags,
  type CurrentUser,
  resolveShellModeFromPath,
  resolveShellVisibility,
  type ShellModeRouteRule,
} from '@zhiyue/core';
import React from 'react';

type SessionSnapshot = {
  currentUser?: CurrentUser | null;
  loading: boolean;
  featureFlags: CoreFeatureFlags;
};

const DEFAULT_FEATURE_FLAGS: CoreFeatureFlags = {
  showHeader: true,
  showNav: true,
  showFooter: true,
  enableTenantSwitch: true,
  enableRegister: false,
};

let sessionSnapshot: SessionSnapshot = {
  currentUser: null,
  loading: true,
  featureFlags: DEFAULT_FEATURE_FLAGS,
};

const listeners = new Set<() => void>();

const SHELL_MODE_RULES: ShellModeRouteRule[] = [
  { path: '/callback', mode: 'canvas-only', exact: true },
  { path: '/user', mode: 'canvas-only' },
];

export const updateSessionSnapshot = (
  snapshot: Partial<SessionSnapshot>,
): void => {
  sessionSnapshot = {
    ...sessionSnapshot,
    ...snapshot,
    featureFlags: {
      ...sessionSnapshot.featureFlags,
      ...(snapshot.featureFlags || {}),
    },
  };
  listeners.forEach((listener) => listener());
};

export const getSessionSnapshot = (): SessionSnapshot => {
  return sessionSnapshot;
};

export const subscribeSessionSnapshot = (
  listener: () => void,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useSessionSnapshot = (): SessionSnapshot => {
  return React.useSyncExternalStore(
    subscribeSessionSnapshot,
    getSessionSnapshot,
    getSessionSnapshot,
  );
};

export const resolveShellMode = (pathname: string): AppShellMode => {
  return resolveShellModeFromPath(pathname, SHELL_MODE_RULES, 'full');
};

export const getLayoutVisibility = (pathname: string) => {
  const mode = resolveShellMode(pathname);
  const { featureFlags } = getSessionSnapshot();
  const visibility = resolveShellVisibility(mode, featureFlags);

  return {
    mode,
    ...visibility,
  };
};
